import Profile from '../models/Profile';
import { baseApi } from 'core/api';

const state = () => ({
    _shown: false,
    _limits: null,
    _limitsLoading: true,
    _limitsCurrentSegment: null,
    _limitsCurrentNumber: null,
    _limitsCurrentPlanId: null,
    _limitsCurrentPlanInfo: null,
    _limitsMaxSegment: null,
    _limitsMaxNumber: null,
    _limitsUpgradeRequired: null,
    _limitsAvailableForUpdatePlans: null,
    _limitsIsMessagingCanadaOnly: null,
    _profile: new Profile({
        type: '10dlc',
        businessProfile: {
            status: 'empty',
        },
        useCase: null,
        businessInformation: null,
        campaigns: [],
        owner: null,
        plan: null,
        brandTwoFaVerification: null,
        should_be_charged: true,
    }),
    _profileDepositBalance: 0,
    _useCases: [],
    _campaignTypes: null,
    _optInFileLoader: {
        size: null,
        isLoading: false,
        progress: 100, // 0 - start loading | 1 - loaded
    },
    _lastSentMessages: [],
    _lastSentMessagesLoading: false,
    _isVerifyEinLoading: false,
});

const getters = {
    dlsLimits: (state) => state._limits,
    dlsLimitsLoading: (state) => state._limitsLoading,
    dlsCurrentSegment: (state) => state._limits && state._limitsCurrentSegment,
    dlcOptInFileLoader: (state) => state._optInFileLoader,
    dlsCurrentNumber: (state) => state._limits && state._limitsCurrentNumber,
    dlsCurrentPlan: (state) => state._limits && state._limitsCurrentPlanInfo,
    dlsCurrentPlanId: (state) => state._limits && state._limitsCurrentPlanId,
    dlsCurrentPlanIsStarter: (state) => state._limits && state._limitsCurrentPlanId === 1,
    dlsCurrentPlanIsStandard: (state) => state._limits && state._limitsCurrentPlanId === 2,
    dlsMaxSegment: (state) => state._limits && state._limitsMaxSegment,
    dlsMaxNumber: (state) => state._limits && state._limitsMaxNumber,
    dlsHasMaxNumber: (state) =>
        state._limits && state._limitsCurrentNumber >= state._limitsMaxNumber,
    dlsHasMaxSegment: (state) =>
        state._limits && state._limitsCurrentSegment >= state._limitsMaxSegment,
    dlsUpgradeRequired: (state) => state._limitsUpgradeRequired,
    dlsAvailableForUpdatePlans: (state) => state._limitsAvailableForUpdatePlans,
    dlsUseCases: (state) => state._useCases,
    dlsCampaignTypes: (state) => state._campaignTypes,
    dlsProfile: (state) => state._profile,
    dlsProfileDepositBalance: (state) => state._profileDepositBalance,
    lastSentMessages: (state) => state._lastSentMessages,
    lastSentMessagesLoading: (state) => state._lastSentMessagesLoading,
    isDlsVerifyEinLoading: (state) => state._isVerifyEinLoading,
    dlsIsMessagingCanadaOnly: (state) => state._limitsIsMessagingCanadaOnly,
};

const actions = {
    updateOrganization10dlcPlanSelected(params) {
        return baseApi.post('core/organization/10-dlc/plan-selected', params).then((response) => {
            return response.data;
        });
    },

    select10dlcSoleNumber(params) {
        return baseApi.post('core/organization/otp-number/save', params).then((response) => {
            return response.data;
        });
    },

    deleteOrganization10dlcStandard() {
        const id = this.dlsProfile.useCase.id;

        if (!id) return;

        return baseApi
            .delete('core/organization/10-dlc/standard', { data: { id } })
            .then((response) => {
                this._setDlcProfile(response.data);

                return response.data;
            });
    },

    deleteOrganization10dlcStandardSimple(params) {
        return baseApi.delete('core/organization/10-dlc/standard', params).then((response) => {
            return response;
        });
    },

    getOrganization10Dlc() {
        if (!this.dlsLimits) return;

        return baseApi.get('core/organization/10-dlc').then((response) => {
            this._setDlcProfile(response.data);
        });
    },

    getOrganization10DlcNew() {
        if (!this.dlsLimits) return;

        return baseApi.get('compliance/services/list').then((response) => {
            this._setDlcProfileNew(response.data);
        });
    },

    setDlcProfileLoadingToFalse() {
        this._setDlcLoadLimitsLoading(false);
    },

    async getOrganization10DlcLimits(useLoading = true) {
        try {
            if (useLoading) {
                this._setDlcLoadLimitsLoading(true);
            }

            const response = await baseApi.get('core/organization/10-dlc/limits');

            this._setDlcLoadLimits(response.data);

            return response.data;
        } catch (e) {
            return false;
        } finally {
            if (useLoading) {
                this._setDlcLoadLimitsLoading(false);
            }
        }
    },

    getOrganization10DlcLimitsPlanTypes() {
        return baseApi.get('core/organization/10-dlc/limits').then((response) => {
            return response.data.dlcPlanTypes;
        });
    },

    getOrganization10DlcLimitsDlcPlan() {
        return baseApi.get('core/organization/10-dlc/limits').then((response) => {
            return response.data.dlcPlan;
        });
    },

    updateDlcLimits(data) {
        this._setDlcLoadLimits(data);
    },

    updateOrganization10DlcAllowUpgrade() {
        return baseApi.patch('core/organization/10-dlc/allow-upgrade').then((response) => {
            this._setDlcUpgradeRequired();

            return response.data;
        });
    },

    updateOptInSamples(data) {
        return baseApi.post('core/organization/opt-in-data', data).then((response) => {
            return response.data;
        });
    },

    updateOptInDataFilesValidate(formData) {
        return baseApi
            .post('core/organization/opt-in-data/files/validate', formData)
            .then((response) => {
                return response.data;
            });
    },

    deleteOptInFiles(id) {
        return baseApi
            .delete(`core/organization/opt-in-data/files?opt_in_file_id=${id}`)
            .then((response) => {
                return response.data;
            });
    },

    uploadOptInFiles(formData) {
        const fileSize = formData.get('fileSize');

        const config = {
            onUploadProgress: (progressEvent) => {
                const progress = Math.trunc((progressEvent.loaded / fileSize) * 100);

                this._setTendlcOptInFileLoading({
                    isLoading: true,
                    progress,
                });
            },
        };

        return baseApi
            .post('core/organization/opt-in-data/files/upload', formData, config)
            .then((response) => {
                return response.data;
            })
            .finally((e) => {
                this._setTendlcOptInFileLoaded();
            });
    },

    updateOrganization10DlcById({ replaceField, form, params }) {
        if (!this.dlsLimits) return;

        return form.post(`core/organization/10-dlc/${replaceField}`, params).then((response) => {
            this._setDlcProfileUpdate(response.data);

            return response.data;
        });
    },

    updateOrganization10DlcByIdValidate({ form, params }) {
        return form
            .post('core/organization/10-dlc/standard/campaigns/validate', params)
            .then((response) => {
                return response.data;
            });
    },

    getOrganization10DlcStates() {
        return baseApi.get('core/organization/10-dlc/states').then((response) => {
            return response.data;
        });
    },

    getOrganization10DlcTaxFee(planId) {
        return baseApi.get(`/billing/preview/dlc?plan=${planId}`, planId).then((response) => {
            return response.data;
        });
    },

    getOrganization10DlcVerifyEin(value) {
        this._setDlcUpdateVerifyEinLoading(true);

        return baseApi
            .get(`core/organization/10-dlc/verify-ein?q=${value}`)
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                error?.response;
            })
            .finally(() => {
                this._setDlcUpdateVerifyEinLoading(false);
            });
    },

    getOrganization10DlcBusinessTypes() {
        return baseApi.get('core/organization/10-dlc/business-types').then((response) => {
            return response.data;
        });
    },

    getOrganization10DlcRegistrationTypes() {
        return baseApi.get('core/organization/10-dlc/registration-types').then((response) => {
            return response.data;
        });
    },

    getOrganization10DlcJobPositions() {
        return baseApi.get('core/organization/10-dlc/job-positions').then((response) => {
            return response.data;
        });
    },

    getOrganization10DlcIndustries() {
        return baseApi.get('core/organization/10-dlc/industries').then((response) => {
            return response.data;
        });
    },

    getOrganization10DlcRegionsOfOperation() {
        return baseApi.get('core/organization/10-dlc/regions-of-operation').then((response) => {
            return response.data;
        });
    },

    getOrganization10DlcCampaignTypes() {
        return baseApi.get('core/organization/10-dlc/campaign-types').then((response) => {
            this._setDlcCampaignTypes(response.data);

            return response.data;
        });
    },

    getOrganization10DlcUseCases() {
        return baseApi.get('core/organization/10-dlc/use-cases').then((response) => {
            this._setDlcUseCases(response.data);

            return response.data;
        });
    },

    updateStateDlcProfile(data) {
        this._setDlcUpdateState(data);
    },

    getLastSentMessages() {
        this._setTendlcLastSentMessagesLoading(true);
        return baseApi
            .get('core/messages/last-outbound')
            .then((response) => {
                this._setTendlcLastSentMessages(response.data);

                return response.data;
            })
            .finally(() => {
                this._setTendlcLastSentMessagesLoading(false);
            });
    },

    _setDlcLoadLimits(limits) {
        this._limits = limits;
        this._limitsCurrentNumber = limits.currentAmount.phoneNumbers;
        this._limitsCurrentSegment = limits.currentAmount.segments;
        this._limitsCurrentPlanId = limits.dlcPlan.planTypeId;
        this._limitsCurrentPlanInfo = limits.dlcPlanTypes[this._limitsCurrentPlanId];
        this._limitsMaxNumber = this._limitsCurrentPlanInfo.phone_numbers_max;
        this._limitsMaxSegment = this._limitsCurrentPlanInfo.segments_to;
        this._limitsUpgradeRequired = limits.dlcPlan.upgradeRequired;
        this._limitsAvailableForUpdatePlans = limits.dlcPlan.availableForUpdatePlans;
        this._limitsIsMessagingCanadaOnly = limits.dlcPlan.isMessagingCanadaOnly;
    },

    _setDlcLoadLimitsLoading(status) {
        this._limitsLoading = status;
    },

    _setTendlcOptInFileLoaded() {
        this._optInFileLoader = {
            size: null,
            isLoading: false,
            progress: 100,
        };
    },

    _setTendlcOptInFileLoading(data) {
        this._optInFileLoader = data;
    },

    _setDlcUpdateState([key, value]) {
        const mappedKey = key.startsWith('_') ? key : `_${key}`;
        this[mappedKey] = value;
    },

    _setDlcUpgradeRequired() {
        this._limitsUpgradeRequired = 1;
    },

    _setDlcUseCases(data) {
        this._useCases = data;
    },

    _setDlcCampaignTypes(data) {
        this._campaignTypes = data;
    },

    _setDlcProfileNew(data) {
        const dlcPlan = data.brand || {};

        this._profile = new Profile({
            type: '10dlc',
            businessProfile: {
                status: dlcPlan.status,
                regProgressStatus: dlcPlan.status,
                planTypeId: dlcPlan.plan_type,
                currentPlanTypeId: dlcPlan.id,
                twilio_service_errors: dlcPlan.registration_errors,
                terms: null,
            },
            useCase: {},
            businessInformation: {
                business_name: '',
                brand_type: '',
                brandScore: '',
            },
            campaigns: data.campaigns || [],
            plan: dlcPlan,
            optInNumber: {},
            owner: null,
            brandTwoFaVerification: {},
        });
    },

    _setDlcProfile(profile) {
        const dlc = profile.dlc || {};
        const optInNumber = profile.otpNumberVerification || {};
        const dlcPlan = profile.dlcPlan || {};
        const dlcPlanType = this._limits.dlcPlanTypes[dlcPlan.plan_type_id];
        const should_be_charged = Boolean(profile.should_be_charged);

        this._profileDepositBalance = profile.deposit_balance;
        this._profile = new Profile({
            type: '10dlc',
            businessProfile: {
                status: dlcPlan.brand_status || dlcPlan.registration_status,
                regProgressStatus: dlcPlan.reg_progress_status,
                planTypeId: dlcPlan.plan_type_id,
                currentPlanTypeId: dlcPlan.current_plan_type_id,
                twilio_service_errors: dlcPlan.twilio_service_errors,
                terms: profile.terms,
            },
            useCase: {
                ...dlc,
            },
            businessInformation: {
                business_name: dlcPlanType.name,
                brand_type: dlcPlanType.name,
                brandScore: dlcPlan.brand_score,
            },
            campaigns: profile.campaigns || [],
            plan: dlcPlan,
            optInNumber: optInNumber,
            owner: profile.owner || null,
            brandTwoFaVerification: profile.brandTwoFaVerification,
            should_be_charged,
        });
    },

    _setDlcProfileUpdate(profile) {
        const dlc = profile.dlc || {};
        const dlcPlan = profile.dlcPlan || {};
        const optInNumber = profile.otpNumberVerification || {};
        const dlcPlanType = this._limits.dlcPlanTypes[dlcPlan.plan_type_id];

        this._profile.campaigns = profile.campaigns || [];
        this._profile.businessProfile = {
            ...this._profile.businessProfile,
            status: dlcPlan.brand_status || dlcPlan.registration_status,
            regProgressStatus: dlcPlan.reg_progress_status,
            planTypeId: dlcPlan.plan_type_id,
            currentPlanTypeId: dlcPlan.current_plan_type_id,
            terms: profile.terms,
        };
        this._profile.useCase = {
            ...dlc,
        };
        this._profile.businessInformation = {
            business_name: dlcPlanType.name,
            brand_type: dlcPlanType.name,
            brandScore: dlcPlan.brand_score,
        };
        this._profile.plan = dlcPlan;
        this._profile.optInNumber = optInNumber;
        this._profile.owner = profile.owner || null;
        this._profile.brandTwoFaVerification = profile.brandTwoFaVerification;
    },

    _setTendlcLastSentMessagesLoading(status) {
        this._lastSentMessagesLoading = status;
    },

    _setTendlcLastSentMessages(data) {
        this._lastSentMessages = data;
    },

    _setDlcUpdateVerifyEinLoading(status) {
        this._isVerifyEinLoading = status;
    },
};

export default {
    state,
    getters,
    actions,
};
